import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useHistory } from 'react-router';
import { Tag, Modal, Icon, message, Tooltip, Text, Button } from 'components';
import styled from 'styled-components';
import moment from 'moment';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types';
import { capitalize } from 'lodash';
import { UserFeatures, userSubRolesToLabelMap } from '@bb/users';
import {
  SupraCoopType,
  SupraSystemSubscriptionType,
} from '@bb/domain/dist/types/SupraSystemSubscription';
import errorReporter from '@app/errorReporter';
import SendSupraInviteModal from 'modals/SendSupraInviteModal';
import { useSendSupraInvite } from 'hooks/useSendSupraInvite';
import {
  SubscriptionContainer,
  SubscriptionDetails,
  SubscriptionLogo,
  SubscriptionText,
  SubscriptionStatus,
  SubscriptionButton,
  SubscriptionButtonContainer,
  SupraKeyDetail,
  SupraKeyDetailLine,
  SupraKeyDetailsContainer,
} from '../styles';
import { featureEnabled, formatPhoneNumber } from '../../../../utils';
import {
  cancelSupraSystemSubscription,
  getCanCreateNonBillableKey,
  removeManualCoop,
} from '../../../../api/SupraSystemSubscriptionController';
import { disableEnableAutoCoopUser } from '../../../../api/UserController';
import supraSystemLogo from '../../../../img/SupraSystemLogo.png';
import MLSUserPageContext from '../MLSUserPageContext';
import SubscribeUserModal from '../../MLSStaffPages/MLSStaffUsersPage/SubscribeUserModal';
import { useActivationFeeContext } from '../../MLSStaffPages/ActivationFeeContext';

type SupraSystemProps = { coopType?: SupraCoopType };

const SupraSystem: React.FC<SupraSystemProps> = ({
  coopType = SupraCoopType.PRIMARY,
}) => {
  const {
    subscriptions,
    setSubscriptions,
    user,
    lockboxes,
    isAutoCoopDisabled,
    setIsAutoCoopDisabled,
    fetchUser,
  } = useContext(MLSUserPageContext);
  const {
    logo,
    name,
    status,
    invitationDate,
    keySerialNumber,
    type,
    billing,
    assignLockboxesReleaseShackles,
    supraOwnerName,
  } = subscriptions.supra;
  const {
    email: userEmail,
    phone: userPhone,
    notes: userNotes,
    subRole: userSubRole,
  } = user || {};

  const history = useHistory();

  const navigateBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const [showInviteModal, setShowInviteModal] = useState(false);

  const [loading, setLoading] = useState<
    | 'subscribe'
    | 'cancelSupraSubscription'
    | 'keyCoopModalConfirm'
    | 'removeManualCoop'
    | ''
  >();
  const [openCancelSubscriptionModal, setOpenCancelSubscriptionModal] =
    useState(false);
  // Track whether our custom subscription modal is open
  const [subscribeModalVisible, setSubscribeModalVisible] = useState(false);
  // Track whether the Enable/Disable Co-op Key modal is open
  const [coopKeyModalVisable, setCoopKeyModalVisible] = useState(false);

  const { activationRate, hasValidFullContract } = useActivationFeeContext();

  const { sendInvite, loading: inviteLoading } = useSendSupraInvite();

  function handleSubscribeModalLoading(isLoading: boolean) {
    setLoading(isLoading ? 'subscribe' : '');
  }
  const [pinVisible, setPinVisible] = useState(false);
  const [removeManualCoopModalOpen, setRemoveManualCoopModalOpen] =
    useState(false);

  const [canCreateNonBillableKeys, setCanCreateNonBillableKey] = useState(true);
  useEffect(() => {
    (async () => {
      if (
        user &&
        user.org.features?.includes('SUPRA_MVP') &&
        featureEnabled('supraAddUserFeature') &&
        subscriptions.supra.status ===
          SupraSystemSubscriptionStatus.unsubscribed
      ) {
        const res = await getCanCreateNonBillableKey(user.org.id);
        setCanCreateNonBillableKey(res);
      }
    })();
  }, [user, subscriptions.supra.status]);

  const sendSupraInvitation = () => {
    setShowInviteModal(true);
  };

  const handleInviteConfirm = async () => {
    if (user) {
      await sendInvite(
        { userId: user.id, orgId: user.org.id },
        () => {
          // onSuccess callback
          setSubscriptions({
            ...subscriptions,
            supra: {
              ...subscriptions.supra,
              logo: supraSystemLogo,
              name: 'Supra System',
              status: SupraSystemSubscriptionStatus.pending,
              invitationDate: new Date().toISOString(),
            },
          });
          setShowInviteModal(false);
        },
        () => {
          // onError callback
          setShowInviteModal(false);
        },
      );
    }
  };

  const handleInviteCancel = () => {
    setShowInviteModal(false);
  };

  const cancelSupraSubscription = async () => {
    if (user) {
      try {
        setLoading('cancelSupraSubscription');

        await cancelSupraSystemSubscription({
          orgId: user.org.id,
          userId: user.id,
        });
        setSubscriptions({
          ...subscriptions,
          supra: {
            ...subscriptions.supra,
            logo: supraSystemLogo,
            name: 'Supra System',
            status: SupraSystemSubscriptionStatus.unsubscribed,
            invitationDate: '',
          },
        });
        message.success('Supra subscription cancelled');
      } catch (e) {
        message.error('Something went wrong please try again');
      } finally {
        setLoading('');
        setOpenCancelSubscriptionModal(false);
      }
    }
  };

  const renderStatusWithTooltip = (
    subscriptionStatus: SupraSystemSubscriptionStatus,
  ) => {
    const getTooltipInfo = (statusValue: SupraSystemSubscriptionStatus) => {
      switch (statusValue) {
        case SupraSystemSubscriptionStatus.suspended:
          return 'Payment is required';
        case SupraSystemSubscriptionStatus.deactivated:
          return 'Payment is required and late fee applies';
        default:
          return null;
      }
    };

    const tooltipTitle = getTooltipInfo(subscriptionStatus);
    const tagColor =
      subscriptionStatus === SupraSystemSubscriptionStatus.subscribed
        ? 'green'
        : 'gray';

    if (tooltipTitle) {
      return (
        <Tooltip placement="right" title={tooltipTitle}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tag color={tagColor}>{capitalize(subscriptionStatus)}</Tag>
            <Icon type="antdInfoCircleOutlined" />
          </span>
        </Tooltip>
      );
    }

    return <Tag color={tagColor}>{capitalize(subscriptionStatus)}</Tag>;
  };

  const disableEnableCoopKey = async (action: 'disable' | 'enable') => {
    try {
      setLoading('keyCoopModalConfirm');
      await disableEnableAutoCoopUser(action, user!.id);
      message.success(`Co-op Key ${action}d`);
      setIsAutoCoopDisabled(action === 'disable');
    } catch (err) {
      errorReporter.error(
        `Error disabling or enabling Auto Co-op Key. Error: ${err}`,
      );
    } finally {
      setLoading('');
      setCoopKeyModalVisible(false);
    }
  };

  const CoopKeyModal = useMemo(
    () => (
      <Modal
        centered
        open={coopKeyModalVisable}
        title={'Are you sure?'}
        footer={[
          <Button
            key="coopKeyModal-button-back"
            onClick={() => setCoopKeyModalVisible(false)}
          >
            Back
          </Button>,
          <Button
            key="coopKeyModal-button-confirm"
            type="primary"
            danger={!isAutoCoopDisabled}
            onClick={() =>
              disableEnableCoopKey(isAutoCoopDisabled ? 'enable' : 'disable')
            }
            loading={loading === 'keyCoopModalConfirm'}
          >
            {isAutoCoopDisabled ? 'Enable Co-op Key' : 'Disable Co-op Key'}
          </Button>,
        ]}
        onCancel={() => setCoopKeyModalVisible(false)}
      >
        {!isAutoCoopDisabled
          ? "By disabling this user's co-op key, they will not be able to access lockboxes in your organization."
          : "By enabling this user's co-op key, they will be able to access lockboxes in your organization."}
      </Modal>
    ),
    [coopKeyModalVisable, isAutoCoopDisabled, loading, user],
  );

  const buildLockboxListItem = (lockbox: any) => (
    <li key={lockbox._id}>
      <FlexColumn>
        <b>{lockbox.serialNumber}</b>
        {lockbox.listing && (
          <SpacedFlexColumn>
            {lockbox.listing.mls_numbers?.length && (
              <div>
                MLS{' '}
                {lockbox.listing.mls_numbers
                  .map((mlsNum: any) => mlsNum.number)
                  .join(', ')}
              </div>
            )}
            {lockbox.listing.line1 && (
              <div>
                {`${lockbox.listing.line1}${
                  lockbox.listing.city ? `, ${lockbox.listing.city}` : ''
                }${
                  lockbox.listing.province
                    ? `, ${lockbox.listing.province}`
                    : ''
                }
            `}
              </div>
            )}
            {lockbox.listing.officePhoneNumber && (
              <div>{formatPhoneNumber(lockbox.listing.officePhoneNumber)}</div>
            )}
          </SpacedFlexColumn>
        )}
      </FlexColumn>
    </li>
  );

  const showSubscriptionButtonContainer = () =>
    (!featureEnabled('supraAutoCoopFeature') ||
      coopType === SupraCoopType.PRIMARY) &&
    status &&
    [
      SupraSystemSubscriptionStatus.subscribed,
      SupraSystemSubscriptionStatus.pending,
      SupraSystemSubscriptionStatus.suspended,
      SupraSystemSubscriptionStatus.deactivated,
    ].includes(status);

  const handleRemoveManualCoop = () => setRemoveManualCoopModalOpen(true);

  const CancelSubscriptionModal = useMemo(
    () => (
      <Modal
        centered
        open={openCancelSubscriptionModal}
        footer={[
          <SubscriptionButton
            key="close-cancel-subscription-modal"
            type="default"
            onClick={() => setOpenCancelSubscriptionModal(false)}
          >
            No, don&apos;t cancel
          </SubscriptionButton>,
          <SubscriptionButton
            key="cancel-subscription-button"
            type="primary"
            onClick={cancelSupraSubscription}
            loading={loading === 'cancelSupraSubscription'}
          >
            Cancel subscription
          </SubscriptionButton>,
        ]}
        width={400}
        onCancel={() => setOpenCancelSubscriptionModal(false)}
      >
        <FlexContainer>
          <StyledIcon
            type="antdInfoCircleOutlined"
            color="gold6"
            style={{ marginRight: '5px' }}
            width="25px"
          />
          <ModalContent>
            <ModalHeader>
              Are you sure you want to cancel this subscription and unassign
              this key from this user?
            </ModalHeader>
            <ModalBody>
              This user may have shackle permissions to other Lockboxes and
              cancelling the subscription will revoke their access.
            </ModalBody>
            {lockboxes.length ? (
              <ModalBody>
                This user still has these lockboxes assigned to them:
                <StyledList>
                  {lockboxes
                    .sort((a, b) => {
                      if (a.listing && !b.listing) return -1;
                      if (!a.listing && b.listing) return 1;
                      return Number(a.serialNumber) - Number(b.serialNumber);
                    })
                    .map(buildLockboxListItem)}
                </StyledList>
                Please have the lockboxes returned by the agent. Then you can
                unassign them and mark them as returned.
              </ModalBody>
            ) : null}
          </ModalContent>
        </FlexContainer>
      </Modal>
    ),
    [openCancelSubscriptionModal, loading, lockboxes, user],
  );

  const RemoveManualCoopModal = useMemo(
    () => (
      <StyledRemoveCoopModal
        open={removeManualCoopModalOpen}
        onCancel={() => setRemoveManualCoopModalOpen(false)}
        width={600}
        footer={[
          <Button
            key="back"
            onClick={() => setRemoveManualCoopModalOpen(false)}
          >
            Back
          </Button>,
          <Button
            key="remove"
            type="primary"
            danger
            loading={loading === 'removeManualCoop'}
            disabled={loading === 'removeManualCoop'}
            onClick={async () => {
              try {
                setLoading('removeManualCoop');
                await removeManualCoop({
                  userId: user!.id,
                  orgId: user!.org.id,
                });
                message.success('Manual co-op key removed');
                setRemoveManualCoopModalOpen(false);
                navigateBack();
              } catch (error) {
                message.error('Failed to remove manual co-op key');
              } finally {
                setLoading('');
              }
            }}
          >
            Remove Co-op Key
          </Button>,
        ]}
        closable
        closeIcon={<Icon type="antdCloseOutlined" width={16} />}
        centered
        title="Are you sure?"
      >
        By removing this manual co-op key, the agent will no longer be able to
        access lockboxes in your organization.
      </StyledRemoveCoopModal>
    ),
    [removeManualCoopModalOpen, user, navigateBack, loading],
  );

  // Memoize the user object passed to the modal.
  const memoizedSubscribeModalUser = useMemo(() => {
    if (!user) {
      return null;
    }
    return {
      id: user.id,
      orgId: user.org.id,
      email: user.email,
      supraSubscriptionStatus:
        status ?? SupraSystemSubscriptionStatus.unsubscribed,
      role: user.role,
    };
  }, [user, status]);

  return (
    <>
      <SubscriptionContainer>
        <div style={{ display: 'flex' }}>
          <SubscriptionDetails>
            <SubscriptionLogo src={logo} />
            <SubscriptionText>{name}</SubscriptionText>
            <SubscriptionStatus>
              {renderStatusWithTooltip(status)}
            </SubscriptionStatus>
          </SubscriptionDetails>
          {showSubscriptionButtonContainer() && (
            <SubscriptionButtonContainer>
              <SubscriptionButton
                onClick={() => setOpenCancelSubscriptionModal(true)}
                loading={loading === 'cancelSupraSubscription'}
              >
                Cancel subscription
              </SubscriptionButton>{' '}
              {status === SupraSystemSubscriptionStatus.pending && (
                <SubscriptionButton
                  onClick={sendSupraInvitation} // This now shows the confirmation modal
                  loading={inviteLoading}
                >
                  {invitationDate && moment().diff(invitationDate, 'days') <= 30
                    ? 'Resend Invite'
                    : 'Send Invite'}
                </SubscriptionButton>
              )}
            </SubscriptionButtonContainer>
          )}{' '}
          {featureEnabled('supraAutoCoopFeature') &&
            coopType === SupraCoopType.AUTO_COOP && (
              <SubscriptionButtonContainer>
                <SupraKeyDetail>
                  <DisableCoopButton
                    type="primary"
                    onClick={() => setCoopKeyModalVisible(true)}
                    danger={!isAutoCoopDisabled}
                    strong
                  >
                    {isAutoCoopDisabled
                      ? 'Enable Co-op Key'
                      : 'Disable Co-op Key'}
                  </DisableCoopButton>
                </SupraKeyDetail>
              </SubscriptionButtonContainer>
            )}
          {featureEnabled('supraManualCoopFeature') &&
            coopType === SupraCoopType.MANUAL_COOP && (
              <SubscriptionButtonContainer>
                <SupraKeyDetail>
                  <DisableCoopButton
                    type="default"
                    danger
                    onClick={handleRemoveManualCoop}
                  >
                    Remove Co-op Key
                  </DisableCoopButton>
                </SupraKeyDetail>
              </SubscriptionButtonContainer>
            )}
          {(!featureEnabled('supraAutoCoopFeature') ||
            coopType === SupraCoopType.PRIMARY) &&
            (!status ||
              status === SupraSystemSubscriptionStatus.unsubscribed) && (
              <SubscriptionButtonContainer>
                <Tooltip
                  title={
                    user && !user.canSubscribeToSupra
                      ? "The Supra Access product must be enabled for this user's brokerage to subscribe."
                      : undefined
                  }
                >
                  <>
                    <SubscriptionButton
                      type="primary"
                      onClick={() => setSubscribeModalVisible(true)}
                      loading={loading === 'subscribe'}
                      disabled={
                        !user?.canSubscribeToSupra ||
                        (!hasValidFullContract &&
                          featureEnabled('keyholderBillingFeature'))
                      }
                    >
                      Subscribe
                    </SubscriptionButton>
                  </>
                </Tooltip>
                <SubscribeUserModal
                  visible={subscribeModalVisible}
                  onClose={() => setSubscribeModalVisible(false)}
                  loading={loading === 'subscribe'}
                  onSuccess={async () => {
                    // refresh user data in state
                    await fetchUser();
                  }}
                  user={memoizedSubscribeModalUser}
                  setLoading={handleSubscribeModalLoading}
                  activationRate={activationRate}
                  canCreateNonBillableKeys={canCreateNonBillableKeys}
                />
              </SubscriptionButtonContainer>
            )}
        </div>
        {user && user.org?.features?.includes('SUPRA_MVP') && (
          <SupraKeyDetailsContainer>
            {(!status ||
              status !== SupraSystemSubscriptionStatus.unsubscribed ||
              ((!featureEnabled('supraAutoCoopFeature') ||
                coopType === SupraCoopType.PRIMARY) &&
                userNotes)) && (
              <SupraKeyDetail data-cy={'supra-subscription-details'}>
                {(!status ||
                  status !== SupraSystemSubscriptionStatus.unsubscribed) && (
                  <>
                    {!featureEnabled('supraAutoCoopFeature') && (
                      <SubscriptionText>Details</SubscriptionText>
                    )}
                    {featureEnabled('supraAutoCoopFeature') &&
                      supraOwnerName && (
                        <SupraKeyDetailLine>
                          <SubscriptionText>
                            Primary Organization:
                          </SubscriptionText>{' '}
                          <span>{supraOwnerName}</span>
                        </SupraKeyDetailLine>
                      )}
                    <SupraKeyDetailLine>
                      <SubscriptionText>Key type:</SubscriptionText>{' '}
                      <span>
                        {capitalize(type?.replaceAll('_', ' '))}
                        {type !== SupraSystemSubscriptionType.FULL &&
                          userSubRole &&
                          ` · ${userSubRolesToLabelMap[userSubRole]}`}
                      </span>
                    </SupraKeyDetailLine>
                  </>
                )}
                {status === SupraSystemSubscriptionStatus.subscribed && (
                  <>
                    <SupraKeyDetailLine>
                      <SubscriptionText>Key Serial Number:</SubscriptionText>{' '}
                      <span>{keySerialNumber}</span>
                    </SupraKeyDetailLine>
                    {(!featureEnabled('supraAutoCoopFeature') ||
                      coopType === SupraCoopType.PRIMARY) && (
                      <SupraKeyDetailLine style={{ alignItems: 'center' }}>
                        <SubscriptionText>PIN:</SubscriptionText>{' '}
                        <StyledPinInput
                          disabled
                          type={pinVisible ? 'text' : 'password'}
                          value={user.supraPin}
                        />
                        <Icon
                          onClick={() => setPinVisible(prev => !prev)}
                          type={pinVisible ? 'eye' : 'antdEyeInvisibleOutlined'}
                        />
                      </SupraKeyDetailLine>
                    )}
                  </>
                )}
                {(!featureEnabled('supraAutoCoopFeature') ||
                  coopType === SupraCoopType.PRIMARY) &&
                  userNotes && (
                    <SupraKeyDetailLine>
                      <span>
                        <SubscriptionText style={{ display: 'inline' }}>
                          Note:
                        </SubscriptionText>
                        {userNotes}
                      </span>
                    </SupraKeyDetailLine>
                  )}
              </SupraKeyDetail>
            )}
            {type &&
              // Full and Managed Access are always billable and don't have customized permissions.
              type === SupraSystemSubscriptionType.LIMITED && (
                <SupraKeyDetail data-cy={'supra-subscription-billing'}>
                  {(!status ||
                    status !== SupraSystemSubscriptionStatus.unsubscribed) && (
                    <>
                      <SubscriptionText>Billing</SubscriptionText>
                      <SupraKeyDetailLine style={{ marginBottom: '20px' }}>
                        {billing ? 'Billable' : 'Non-billable'}
                      </SupraKeyDetailLine>
                    </>
                  )}
                  <SubscriptionText>Customized permissions</SubscriptionText>
                  <Text color="#a6a6a6">
                    Assigned lockboxes and release shackle
                  </Text>
                  <Text block color="#333">
                    {assignLockboxesReleaseShackles ? 'Yes' : 'No'}
                  </Text>
                  <Text color="#a6a6a6">Scheduling services</Text>
                  <Text block color="#333">
                    {user.features?.includes(
                      UserFeatures['Scheduling Services'],
                    )
                      ? 'Yes'
                      : 'No'}
                  </Text>
                </SupraKeyDetail>
              )}
          </SupraKeyDetailsContainer>
        )}
      </SubscriptionContainer>

      <SendSupraInviteModal
        open={showInviteModal}
        loading={inviteLoading}
        userEmail={userEmail}
        userPhone={userPhone}
        onConfirm={handleInviteConfirm}
        onCancel={handleInviteCancel}
      />

      {CancelSubscriptionModal}
      {RemoveManualCoopModal}
      {featureEnabled('supraAutoCoopFeature') &&
        coopType !== SupraCoopType.PRIMARY &&
        CoopKeyModal}
    </>
  );
};

const StyledPinInput = styled.input`
  background: none !important;
  border: 0;
  padding: 0;
  color: #000;
  width: 50px;
`;

const StyledIcon = styled(Icon)`
  margin: 0 10px;
`;

const FlexContainer = styled.div`
  display: flex;
`;

const FlexColumn = styled(FlexContainer)`
  flex-direction: column;
`;

const SpacedFlexColumn = styled(FlexColumn)`
  margin-bottom: 16px;
`;

const ModalHeader = styled.div`
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
`;
const ModalBody = styled.div`
  color: rgba(0, 0, 0, 0.65);
`;
const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  color: rgba(0, 0, 0, 0.65);
  margin: 0 10px;
  gap: 10px;
`;

const StyledRemoveCoopModal = styled(Modal)`
  .ant-modal-close {
    display: block;
    color: #333 !important;
  }
`;

const StyledList = styled.ul`
  list-style-type: disc;
  padding-left: 16px;
  margin: 1rem 0;
  max-height: 30rem;
  overflow-y: auto;
`;

const DisableCoopButton = styled(Button)`
  float: right;
`;

export default SupraSystem;
